/**
 * Shop CRUD via Supabase.
 * Replaces Express backend GET/POST /api/shops
 */
import { supabase } from '../lib/supabase';
import { apiFetch, ApiError } from './api-client';
import { normalizeAddress } from '../lib/shop-address';

export interface Shop {
  id: string;
  onchain_id: string | null;
  owner_address: string;
  name: string;
  description: string;
  location: string;
  image_url: string;
  pixel_image_url: string;
  is_active: boolean;
  is_verified: boolean;
  reward_tiers?: RewardTier[];
  latitude?: number | null;
  longitude?: number | null;
  lore_mode?: 'default' | 'custom';
  stamp_icon?: string;
  custom_lore?: LoreOverride[] | null;
  beans?: ShopBean[] | null;
  specials?: ShopSpecial[] | null;
  stamp_eligible_item_ids?: string[] | null;
  discovery_listed?: boolean;
  accent_color?: string | null;   // '#rrggbb' storefront tint; null = palette default
  currency_name?: string | null;  // per-shop points label; null = 'BEANS'
  birthday_perk?: BirthdayPerk | null;
  stamp_windows?: StampWindow[] | null;
  first_visit_double?: boolean;
  reward_label?: string | null; // client-readable — in SHOP_PUBLIC_COLUMNS
  weekly_goal?: WeeklyGoal | null;
  stamp_milestones?: StampMilestone[] | null;
  /** On-chain card size MIRROR (061). Null = never set; client falls back to 9. */
  free_every_n?: number | null;
  created_at: string;
  // redemption_secret is deliberately NOT here: it is never client-readable,
  // and is excluded from SHOP_PUBLIC_COLUMNS on purpose. See SAFE_SHOP_COLS
  // in web/api/shops/register.ts for the owner-gated projection that may
  // carry it.
}

export interface BirthdayPerk {
  enabled: boolean;
  title: string;        // what the counter honors, e.g. "Free birthday muffin"
  bonus_stamps: number; // 0-2 auto-added once per year during the window
}

export interface StampWindow {
  day: number;          // 0=Sun … 6=Sat, shop-local
  start: string;        // 'HH:MM'
  end: string;          // 'HH:MM'
  multiplier?: number;  // 2
}

export interface ShopSpecial {
  id: string;
  title: string;
  detail?: string;
  valid_until: string;              // ISO — expired specials never render
  square_variation_ids?: string[];  // links the special to Square items (bonus)
  bonus_stamps?: number;            // 0-2 extra stamps for buying it
}

export type BeanMethod = 'espresso' | 'filter' | 'decaf' | 'other';
export interface ShopBean {
  method: BeanMethod;
  roaster: string;
  name: string;
}

/**
 * A shop's weekly serve target, shown to customers on HOME and to the owner
 * on the stats page. Resets Monday 00:00 in the SHOP's timezone.
 */
export interface WeeklyGoal {
  enabled: boolean;
  target: number;
  /**
   * Display only. The counter is TOTAL STAMPS, whatever the drink — NFC and
   * barista taps carry no item identity, so a per-item count is impossible
   * outside Square-connected shops. The label reads specific; the number is
   * honest.
   */
  label: string;
}

/** A one-off perk earned at N LIFETIME stamps at this shop. */
export interface StampMilestone {
  at: number;
  name: string;
}

export interface RewardTier {
  id: string;
  name: string;
  description: string;
  pointsCost: number;
  rewardType: string;
}

// Every column the client is allowed to read. Migration 029 enforces this
// list with column-level grants — a `select('*')` from the anon key now
// FAILS (42501). Keep in sync with the GRANT in 029_shops_column_grants.sql.
//
// DO NOT add avg_coffee_price_cents back: 045 revokes it from `authenticated`,
// and Postgres rejects the entire query if any referenced column is ungranted
// — so one extra name here 42501s every browser read of a shop, including
// QR-scan joining. Owner-only columns go through getMyShopPrivate().
export const SHOP_PUBLIC_COLUMNS =
  'id, onchain_id, owner_address, name, description, location, image_url, ' +
  'pixel_image_url, is_active, is_verified, created_at, tier, founding_number, ' +
  // NOTE: reward_tiers restored by 033 (applied to prod BEFORE this code
  // deploys — verify with information_schema if in doubt).
  'tier_started_at, at_risk_days, timezone, reward_tiers, latitude, longitude, ' +
  'lore_mode, custom_lore, stamp_icon, beans, specials, stamp_eligible_item_ids, reward_label, ' +
  'discovery_listed, accent_color, currency_name, birthday_perk, stamp_windows, first_visit_double, ' +
  // Granted by 060 (weekly_goal, stamp_milestones) and 061 (free_every_n).
  // Adding a name here without the matching GRANT 42501s EVERY browser read
  // of a shop, not just this column.
  'weekly_goal, stamp_milestones, free_every_n';

// Directory projection for the browse-all FIND A SHOP list. Deliberately
// slimmer than SHOP_PUBLIC_COLUMNS: bulk enumeration must not ship
// owner_address (wallets) or avg_coffee_price_cents (pricing) to every
// browser — the leak fix in DISCOVERY_VISIBILITY_DECISION.md. The owner is
// resolved per-shop at selection time via getShopById.
export const SHOP_DIRECTORY_COLUMNS =
  'id, name, description, image_url, pixel_image_url, latitude, longitude, accent_color, currency_name, specials';

/** The owner-only columns, deliberately absent from SHOP_PUBLIC_COLUMNS. */
export interface ShopPrivateSettings {
  id: string;
  avg_coffee_price_cents: number | null;
  redemption_secret: string | null;   // ungranted column; only via this RPC
  reward_label: string | null;
}

/**
 * The caller's own shop's private columns, via the SECURITY DEFINER RPC added
 * in 045. Takes no argument on purpose: the function filters on the caller's
 * own `sui_address` JWT claim, so a client cannot ask for someone else's row —
 * that is the whole reason the columns are revoked from `authenticated`.
 *
 * Returns null both when the caller owns no shop and when the RPC fails; this
 * feeds a settings form that already falls back to a default price.
 */
export async function getMyShopPrivate(): Promise<ShopPrivateSettings | null> {
  const { data, error } = await supabase.rpc('get_my_shop_private');
  if (error || !data || data.length === 0) return null;
  return data[0] as ShopPrivateSettings;
}

export async function getShopByOwner(ownerAddress: string): Promise<Shop | null> {
  // Normalize before the strict `.eq` — Supabase stores lowercase, but callers
  // pass addresses from on-chain reads / URLs / paste that may differ by case.
  // An un-normalized miss here is what used to trigger the address-as-name bug.
  const { data, error } = await supabase
    .from('shops')
    .select(SHOP_PUBLIC_COLUMNS)
    .eq('owner_address', normalizeAddress(ownerAddress))
    .single();

  if (error || !data) return null;
  return data as unknown as Shop;
}

/**
 * Like getShopByOwner but a query FAILURE (auth 401, network, RLS) throws
 * instead of returning null. "No rows" (PGRST116) still resolves to null.
 * Use where "you have no shop" and "the lookup broke" must render differently
 * — collapsing the two is how a transient 401 shows "NO SHOP REGISTERED" to
 * an owner who very much has one.
 */
export async function getShopByOwnerStrict(ownerAddress: string): Promise<Shop | null> {
  const { data, error } = await supabase
    .from('shops')
    .select(SHOP_PUBLIC_COLUMNS)
    .eq('owner_address', normalizeAddress(ownerAddress))
    .maybeSingle();

  if (error) throw new Error(error.message || 'shop lookup failed');
  return (data as unknown as Shop) || null;
}

// Directory rows: the slim projection above, never the full Shop shape.
export type ShopDirectoryEntry = Pick<
  Shop,
  'id' | 'name' | 'description' | 'image_url' | 'pixel_image_url' | 'latitude' | 'longitude' | 'accent_color' | 'currency_name' | 'specials'
>;

export async function getAllShops(): Promise<ShopDirectoryEntry[]> {
  const { data, error } = await supabase
    .from('shops')
    .select(SHOP_DIRECTORY_COLUMNS)
    .eq('is_active', true)
    .eq('discovery_listed', true)
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data as unknown as ShopDirectoryEntry[];
}

/**
 * Full public row for ONE shop, by directory id — the selection-time
 * counterpart to getAllShops' slim projection (this is where the customer
 * "learns" the shop's owner address, same as scanning its QR at the till).
 */
export async function getShopById(id: string): Promise<Shop | null> {
  const { data, error } = await supabase
    .from('shops')
    .select(SHOP_PUBLIC_COLUMNS)
    .eq('id', id)
    .single();

  if (error || !data) return null;
  return data as unknown as Shop;
}

/**
 * Exact lifetime count of stamps issued at one shop.
 *
 * `head: true` makes this an index-backed COUNT — one round trip, no rows come
 * back, so it is safe for the operational tab in a way a `purchase_events` row
 * scan is not.
 *
 * Returns `null` (not 0) when the query fails, so callers can render "unknown"
 * instead of confidently displaying a wrong zero if RLS or the network bites.
 * The shop owner is allowed to read these rows via the "Shop owner reads shop
 * purchases" policy in migration 006.
 */
export async function countShopPurchases(shopId: string): Promise<number | null> {
  const { count, error } = await supabase
    .from('purchase_events')
    .select('id', { count: 'exact', head: true })
    .eq('shop_id', shopId);

  if (error) return null;
  return count ?? 0;
}

/**
 * Exact lifetime count of rewards redeemed at one shop.
 *
 * Same shape as `countShopPurchases`: index-backed COUNT, `null` on failure so
 * callers can show "unknown" rather than a wrong zero. RLS gates these rows on
 * `shop_owner_address` ("Shop owner reads shop redeem requests", migration 006),
 * so an owner sees only their own.
 */
export async function countShopRedemptions(shopId: string): Promise<number | null> {
  const { count, error } = await supabase
    .from('redeem_requests')
    .select('id', { count: 'exact', head: true })
    .eq('shop_id', shopId);

  if (error) return null;
  return count ?? 0;
}

export async function upsertShop(shop: {
  onchain_id?: string;
  owner_address: string;
  name: string;
  description?: string;
  location?: string;
  image_url?: string;
  pixel_image_url?: string;
  latitude?: number | null;
  longitude?: number | null;
}): Promise<Shop> {
  // Goes through apiFetch, which attaches the session bearer.
  //
  // This used to be a bare fetch() with only a Content-Type header, and it had
  // never sent a token. `register` gained verifyAuth in 799f9f7 (2026-07-28,
  // "close two unauthenticated endpoints") and the client was not updated with
  // it — the same oversight in the same commit that 401'd every upload-logo
  // caller for nine days. So EVERY shop registration has 401'd since, and the
  // owner was shown "created on-chain but failed to save to database".
  //
  // It went unnoticed because the shop-reconcile cron mirrors new on-chain
  // shops into Supabase within 5 minutes, so the row appeared anyway and only
  // the error screen was wrong. The two shops registered after that commit
  // share an identical created_at — a librarian bulk insert, not two humans.
  try {
    return await apiFetch<Shop>('/api/shops/register', {
      action: 'register',
      onchain_id: shop.onchain_id || null,
      owner_address: shop.owner_address,
      name: shop.name,
      description: shop.description || '',
      location: shop.location || '',
      image_url: shop.image_url || '',
      pixel_image_url: shop.pixel_image_url || '',
      latitude: shop.latitude ?? null,
      longitude: shop.longitude ?? null,
    });
  } catch (err) {
    console.error('Shop upsert failed:', err);
    if (err instanceof ApiError) {
      // apiFetch sets `message` from the payload's `error` field, which for
      // this endpoint is a machine token ("shop_already_registered"). The
      // human sentence lives in `detail`, so prefer it — otherwise the owner
      // is shown a raw enum where a plain explanation was already written for
      // them. Seen live on 2026-08-12: a 409 rendered as the bare token.
      const detail = (err.body as { detail?: unknown } | undefined)?.detail;
      throw new Error(typeof detail === 'string' && detail ? detail : err.message);
    }
    throw err instanceof Error ? err : new Error('Shop registration failed');
  }
}

export interface LoreOverride {
  id: string;
  title: string;
  text: string;
}

export interface ShopSettingsPatch {
  shopId: string;
  at_risk_days?: number;
  timezone?: string;
  reward_tiers?: RewardTier[];
  lore_mode?: 'default' | 'custom';
  stamp_icon?: string;
  custom_lore?: LoreOverride[];
  avg_coffee_price_cents?: number;
  beans?: ShopBean[];
  specials?: ShopSpecial[];
  stamp_eligible_item_ids?: string[];
  discovery_listed?: boolean;
  accent_color?: string | null;   // null clears back to the palette default
  currency_name?: string | null;  // null clears back to 'BEANS'
  birthday_perk?: BirthdayPerk | null;
  stamp_windows?: StampWindow[] | null;
  first_visit_double?: boolean;
  redemption_secret?: string | null;  // null clears it
  reward_label?: string | null;       // null falls back to 'FREE COFFEE'
  weekly_goal?: WeeklyGoal;
  stamp_milestones?: StampMilestone[];
  free_every_n?: number;
  // Cloudinary URLs from /api/shops/upload-logo — server rejects other hosts.
  image_url?: string;
  pixel_image_url?: string;
}

/**
 * Throws on failure rather than returning null.
 *
 * It used to swallow the error and return null, so every caller's
 * `if (!result) throw new Error('Save failed')` replaced a precise
 * server-side reason with two useless words. When `update-settings` was
 * silently dropping weekly_goal/stamp_milestones (see the note in
 * api/_lib/shop-field-validation.ts) it answered `no_updates_provided` — the
 * exact diagnosis — and the owner was shown "Save failed" for a day.
 *
 * Every settings caller already runs inside a try/catch that surfaces
 * `err.message` to the owner, so the reason now reaches the screen.
 */
export async function updateShopSettings(patch: ShopSettingsPatch): Promise<Shop> {
  try {
    return await apiFetch<Shop>('/api/shops/register', {
      action: 'update-settings',
      ...patch,
    });
  } catch (err) {
    console.error('Shop settings update failed:', err);
    if (err instanceof ApiError) throw new Error(err.message);
    throw err instanceof Error ? err : new Error('Save failed');
  }
}

export interface CollectibleItemPatch {
  slug?: string; name: string; description: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary'; icon: string;
}

export async function saveCustomCollectibles(shopId: string, items: CollectibleItemPatch[]): Promise<boolean> {
  try {
    await apiFetch('/api/shops/register', { action: 'custom-collectibles', shopId, items });
    return true;
  } catch (err) {
    console.error('saveCustomCollectibles failed:', err);
    return false;
  }
}

export async function getShopCollectibles(shopId: string): Promise<CollectibleItemPatch[]> {
  const { data, error } = await supabase.from('collectibles_catalog')
    .select('slug, name, description, rarity, icon')
    .eq('shop_id', shopId).eq('active', true);
  if (error || !data) return [];
  return data as CollectibleItemPatch[];
}

/** The shop's anti-phishing word, or null when unset / not permitted / offline. */
export async function getRedemptionSecret(shopId: string): Promise<string | null> {
  try {
    const r = await apiFetch<{ secret: string | null }>('/api/shops/register', {
      action: 'redemption-secret', shopId,
    });
    return r?.secret ?? null;
  } catch {
    // Never block a redemption on this — the card degrades to no secret block.
    return null;
  }
}
